import type {
  CasProgressDetail,
  LearningProgressDetail as LearningProgressDetailData,
  WorkbookProgressDetail,
  WorksheetProgressDetail,
} from '@mathflat/domain/StudentHistory'
import { tJosa } from '@mathflat/shared/i18n/tJosa'

import ExtendedMap from '@/models/ExtendedMap'

import { DTO } from '../DTO'

type Key = LearningProgressDetail['studentBookId']

const progressStatus = {
  INCOMPLETE: tJosa('출제'),
  COMPLETE: '채점 완료',
  PROGRESS: '채점 중',
} as const

export class LearningProgressDetailColl extends DTO {
  map = new ExtendedMap<
    Key,
    WorksheetLearningProgressDetail | WorkbookLearningProgressDetail | CasLearningProgressDetail
  >()

  constructor(arr?: LearningProgressDetailData[]) {
    super()

    if (arr) {
      this.map.addFromArr(
        arr.map((item) => {
          if (item.bookType === 'WORKSHEET') return new WorksheetLearningProgressDetail(item)
          if (item.bookType === 'CAS') return new CasLearningProgressDetail(item)
          return new WorkbookLearningProgressDetail(item)
        }),
        'studentBookId',
      )
    }
  }

  get toArr() {
    return [...this.map.values()]
  }

  get(key: Key) {
    return this.map.get(key)
  }

  clear() {
    this.map.clear()
  }
}

// 진도 - 교재/학습지 공통
export class LearningProgressDetail extends DTO {
  studentBookId: number
  studentName: string
  assignedCount: number
  correctCount: number
  wrongCount: number
  updateDatetime?: Date

  constructor(data: LearningProgressDetailData) {
    super()

    this.studentBookId = data.studentBookId
    this.studentName = data.studentName
    this.assignedCount = data.assignedCount
    this.correctCount = data.correctCount
    this.wrongCount = data.wrongCount
    if ('updateDatetime' in data && data.updateDatetime) {
      this.updateDatetime = new Date(data.updateDatetime)
    }
  }

  // 그룹 학습내역 도넛에 쓰일 값. 채점기록(markingCount)이 없으면 undefined 리턴 -> '-'로 출력
  // 정답률 : 요청한 기간 내 맞은 문제 수 / 채점 완료된 문제 수
  get correctRatio() {
    if (this.markingCount > 0) {
      return Math.floor(
        this.assignedCount !== 0
          ? (this.correctCount / (this.correctCount + this.wrongCount)) * 100
          : 0,
      )
    }
    return undefined
  }

  get markingCount() {
    return this.correctCount + this.wrongCount
  }
}

// 진도 - 학습지

export class WorksheetLearningProgressDetail extends LearningProgressDetail {
  bookType = 'WORKSHEET'
  status: WorksheetProgressDetail['status']
  score: WorksheetProgressDetail['score']

  constructor(data: WorksheetProgressDetail) {
    super(data)

    this.status = data.status
    this.score = data.score
  }

  get koStatus() {
    return progressStatus[this.status]
  }
}

// 진도 - 교재

export class WorkbookLearningProgressDetail extends LearningProgressDetail {
  bookType = 'WORKBOOK'
  page: WorkbookProgressDetail['page']
  progressIdList: WorkbookProgressDetail['progressIdList']

  constructor(data: WorkbookProgressDetail) {
    super(data)

    this.page = data.page
    this.progressIdList = data.progressIdList
  }

  // 교재는 status값 주지 않는다.
  get koStatus() {
    if (this.markingCount === 0) return progressStatus['INCOMPLETE']
    if (this.markingCount === this.assignedCount) return progressStatus['COMPLETE']
    if (this.markingCount < this.assignedCount) return progressStatus['PROGRESS']
  }
}

// 진도 - 자동시험
// CAS 응답 status enum 은 학습지(WORKSHEET)와 달라 별도 매핑한다.
export class CasLearningProgressDetail extends LearningProgressDetail {
  bookType = 'CAS'
  status: CasProgressDetail['status']
  assignedAt: Date
  completedAt: Date | null

  constructor(data: CasProgressDetail) {
    super(data)
    this.status = data.status
    this.assignedAt = new Date(data.assignedAt)
    this.completedAt = data.completedAt ? new Date(data.completedAt) : null
  }

  get koStatus() {
    switch (this.status) {
      case 'NOT_STARTED':
        return progressStatus.INCOMPLETE
      case 'IN_PROGRESS':
        return progressStatus.PROGRESS
      case 'COMPLETED':
        return progressStatus.COMPLETE
    }
  }
}
